// Winner-first merge of two rows that are the same listing.
//
//   1. GET  /listings/{id}/merge-with/{otherId}  -> both rows + per-field comparison
//   2. user picks the WINNER (the row that survives)
//   3. every field defaults to the winner's own value, including status and price
//   4. user may pull individual fields from the loser
//   5. POST /listings/{id}/merge-apply -> writes the picks, retires the loser
//
// Why the winner is step 1: the two older merge UIs decided the survivor from whichever
// listing the analyst happened to have open. At 305 2ND AVE #716 that meant a PERM OFF MKT
// row absorbed the live ACTIVE one. Here the survivor is an explicit choice, pre-selected to
// the live row, and the losing id is named on the button.
//
// Deliberately the same shape as AutoVerifyModal (chip radios, one row per field, amber dot
// on a changed row) because analysts already read that screen.
import React, { useEffect, useState } from "react";
import { authFetch } from "../api";

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  return String(v);
};

const LIVE = ["ACTIVE", "BACK ON MKT", "COMING SOON"];

export default function MergeListingsModal({ listingId, otherId, onClose, onMerged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [winner, setWinner] = useState(null);
  const [picks, setPicks] = useState({});      // field -> listing id (string)
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const res = await authFetch(`/listings/${listingId}/merge-with/${otherId}`);
        if (dead) return;
        setData(res);
        setWinner(res.suggested_winner);
      } catch (e) {
        if (!dead) setError(e.message || "Could not load the comparison");
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [listingId, otherId]);

  // Declared before apply() uses them: data is null until the fetch lands, so both stay
  // empty on the loading/error returns below and apply() is unreachable there anyway.
  const ids = data ? Object.keys(data.listings) : [];
  const loserId = ids.find((i) => i !== winner);

  // Changing the winner resets every field back to that winner's values — picks made
  // against the previous winner would silently invert meaning otherwise.
  const chooseWinner = (id) => { setWinner(id); setPicks({}); };

  const apply = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`/listings/${listingId}/merge-apply`, {
        method: "POST",
        body: JSON.stringify({
          winner_id: Number(winner),
          loser_id: Number(loserId),
          picks: Object.fromEntries(
            Object.entries(picks).filter(([, v]) => v !== winner)
          ),
        }),
      });
      onMerged?.(res);
      onClose?.();
    } catch (e) {
      setError(e.message || "Merge failed");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div style={S.overlay} onClick={onClose}>
        <div style={S.panel} onClick={(e) => e.stopPropagation()}>
          <div style={S.loadingWrap}><div style={S.spinner} /></div>
        </div>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div style={S.overlay} onClick={onClose}>
        <div style={S.panel} onClick={(e) => e.stopPropagation()}>
          <div style={S.header}><div style={S.title}>Merge listings</div>
            <button onClick={onClose} style={S.close}>&times;</button></div>
          <div style={S.body}><div style={S.error}>{error}</div></div>
        </div>
      </div>
    );
  }

  const changed = Object.entries(picks).filter(([, v]) => v !== winner);

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.panel} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <div>
            <div style={S.title}>Merge duplicate listings</div>
            <div style={S.sub}>{data.address}</div>
          </div>
          <button onClick={onClose} style={S.close}>&times;</button>
        </div>

        <div style={S.body}>
          {/* Step 1 — the survivor */}
          <div style={S.stepLabel}>1 · Which listing survives?</div>
          <div style={S.winnerRow}>
            {ids.map((id) => {
              const l = data.listings[id];
              const sel = winner === id;
              const live = LIVE.includes(l.listing_status);
              return (
                <label key={id} style={{ ...S.winnerCard,
                  borderColor: sel ? "#2e7d32" : "#ddd",
                  background: sel ? "#edf7ed" : "#fff" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input type="radio" name="winner" checked={sel}
                           onChange={() => chooseWinner(id)} />
                    <strong style={{ fontSize: 13 }}>#{l.unit_number || "—"}</strong>
                    <span style={{ ...S.statusChip,
                      background: live ? "#e8f5e9" : "#fbe9e7",
                      color: live ? "#2e7d32" : "#c62828" }}>{l.listing_status}</span>
                  </div>
                  <div style={S.cardMeta}>
                    {l.list_price != null ? `$${Number(l.list_price).toLocaleString()}` : "no price"}
                    {" · "}listed {fmt(l.created_date)}
                    {l.active_dom != null ? ` · DOM ${l.active_dom}` : ""}
                  </div>
                  <div style={S.cardMeta}>listing {id} · {l.internal_status}</div>
                  {sel && <div style={S.keepTag}>KEEPS THIS ROW</div>}
                </label>
              );
            })}
          </div>
          <div style={S.hint}>Suggested: {data.suggested_reason}</div>

          {/* Step 2 — the values */}
          <div style={{ ...S.stepLabel, marginTop: 18 }}>
            2 · Values — defaults to the winner, take anything better from the other
          </div>
          <div style={S.tableHead}>
            <div style={{ flex: "0 0 140px" }}>Field</div>
            <div style={{ flex: 1 }}>Value to keep</div>
          </div>
          {data.fields.filter((f) => !f.empty).map((f) => {
            const isChanged = picks[f.field] && picks[f.field] !== winner;
            return (
              <div key={f.field} style={{ ...S.row, background: isChanged ? "#fff8e1" : "transparent" }}>
                <div style={S.rowLabel}>
                  {f.label}
                  {isChanged && <span style={S.changedDot} title="will change" />}
                </div>
                <div style={S.opts}>
                  {!f.differs && <span style={S.same}>{fmt(f.values[winner])} <span style={S.sameTag}>same</span></span>}
                  {f.differs && ids.map((id) => {
                    const sel = (picks[f.field] || winner) === id;
                    return (
                      <label key={id} style={{ ...S.opt,
                        borderColor: sel ? "#1976d2" : "#ddd",
                        background: sel ? "#e3f2fd" : "#fff",
                        fontWeight: sel ? 600 : 400 }}>
                        <input type="radio" name={f.field} checked={sel} style={{ marginRight: 5 }}
                               onChange={() => setPicks((p) => ({ ...p, [f.field]: id }))} />
                        {fmt(f.values[id])}
                        {id === winner && <span style={S.winTag}>winner</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div style={S.footer}>
          <div style={{ flex: 1, fontSize: 12, color: "#555" }}>
            Keeps <strong>{winner}</strong>
            {changed.length > 0 && <> · pulls <strong>{changed.length}</strong> field{changed.length === 1 ? "" : "s"} from {loserId}</>}
            {" · "}retires <strong>{loserId}</strong> (removed from the pipeline and from comps)
          </div>
          <button onClick={onClose} style={S.cancelBtn} disabled={busy}>Cancel</button>
          <button onClick={apply} disabled={busy || !winner}
                  style={{ ...S.applyBtn, background: busy ? "#9e9e9e" : "#2e7d32" }}>
            {busy ? "Merging…" : "Merge"}
          </button>
        </div>
        {error && <div style={{ ...S.error, margin: "0 18px 12px" }}>{error}</div>}
      </div>
      <style>{`@keyframes mlspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const S = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 },
  panel: { background: "#fff", borderRadius: "10px", width: "780px", maxWidth: "94vw", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 12px 48px rgba(0,0,0,0.3)" },
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #eee" },
  title: { fontWeight: 600, fontSize: "15px" },
  sub: { fontSize: "12px", color: "#777", marginTop: 2 },
  close: { border: "none", background: "none", fontSize: "24px", lineHeight: 1, cursor: "pointer", color: "#888" },
  body: { padding: "14px 18px", overflowY: "auto", flex: 1 },
  loadingWrap: { display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 0" },
  spinner: { width: "34px", height: "34px", border: "3px solid #e0e0e0", borderTopColor: "#1976d2", borderRadius: "50%", animation: "mlspin 0.9s linear infinite" },
  error: { padding: "12px", background: "#fdecea", color: "#c62828", borderRadius: "6px", fontSize: "13px" },
  stepLabel: { fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#888", fontWeight: 700, marginBottom: 8 },
  winnerRow: { display: "flex", gap: "10px" },
  winnerCard: { flex: 1, border: "2px solid #ddd", borderRadius: "8px", padding: "10px 12px", cursor: "pointer", position: "relative" },
  statusChip: { fontSize: "10px", fontWeight: 700, padding: "2px 6px", borderRadius: "10px" },
  cardMeta: { fontSize: "11px", color: "#666", marginTop: 4 },
  keepTag: { position: "absolute", top: -9, right: 8, background: "#2e7d32", color: "#fff", fontSize: "9px", fontWeight: 700, padding: "2px 6px", borderRadius: "8px", letterSpacing: "0.04em" },
  hint: { fontSize: "11px", color: "#888", marginTop: 6 },
  tableHead: { display: "flex", gap: "10px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#999", padding: "0 0 6px", borderBottom: "1px solid #eee" },
  row: { display: "flex", gap: "10px", alignItems: "flex-start", padding: "7px 4px", borderBottom: "1px solid #f2f2f2" },
  rowLabel: { flex: "0 0 140px", fontSize: "12px", fontWeight: 600, color: "#333", display: "flex", alignItems: "center" },
  changedDot: { display: "inline-block", width: "7px", height: "7px", borderRadius: "50%", background: "#ffb300", marginLeft: "6px" },
  opts: { flex: 1, display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" },
  opt: { display: "flex", alignItems: "center", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "14px", fontSize: "12px", cursor: "pointer" },
  same: { fontSize: "12px", color: "#777" },
  sameTag: { fontSize: "9px", color: "#aaa", border: "1px solid #e0e0e0", borderRadius: "6px", padding: "0 3px", marginLeft: 4 },
  winTag: { fontSize: "9px", color: "#2e7d32", border: "1px solid #a5d6a7", borderRadius: "6px", padding: "0 3px", marginLeft: 4 },
  footer: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 18px", borderTop: "1px solid #eee" },
  cancelBtn: { padding: "7px 14px", border: "1px solid #ccc", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "13px" },
  applyBtn: { padding: "7px 18px", border: "none", borderRadius: "6px", color: "#fff", fontSize: "13px", fontWeight: 600, cursor: "pointer" },
};
